import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Award, BookOpen, Users, MoreHorizontal } from 'lucide-react'

interface Instructor {
  _id: string
  name: string
  bio: string
  expertise: string[]
  profilePhoto?: string
  socialLinks?: {
    facebook?: string
    instagram?: string
    linkedin?: string
  }
  coursesCount: number
  studentsCount: number
  rating: number
  isActive: boolean
}

export default function AdminInstructors() {
  const navigate = useNavigate()
  const [instructors, setInstructors] = useState<Instructor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    expertise: '',
    profilePhoto: '',
    socialLinks: { facebook: '', instagram: '', linkedin: '' }
  })

  useEffect(() => {
    fetchInstructors()
  }, [])

  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/instructors/admin', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setInstructors(data.instructors || [])
      }
    } catch (error) {
      console.error('Error fetching instructors:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/instructors/admin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          expertise: formData.expertise.split(',').map(e => e.trim())
        })
      })

      if (response.ok) {
        alert('Instructor added successfully!')
        setShowForm(false)
        fetchInstructors()
        setFormData({
          name: '',
          bio: '',
          expertise: '',
          profilePhoto: '',
          socialLinks: { facebook: '', instagram: '', linkedin: '' }
        })
      }
    } catch (error) {
      console.error('Error adding instructor:', error)
      alert('Error adding instructor')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this instructor?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/instructors/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setInstructors(instructors.filter(i => i._id !== id))
        alert('Instructor deleted successfully')
      }
    } catch (error) {
      console.error('Error deleting instructor:', error)
      alert('Error deleting instructor')
    }
  }

  const filteredInstructors = instructors.filter(instructor =>
    instructor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    instructor.expertise.some(e => e.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Toolbar */}
      <div className="bg-white dark:bg-dark-800 rounded-3xl p-4 shadow-sm border border-gray-100 dark:border-dark-700">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
            <input
              type="text"
              placeholder="Search instructors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
            />
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
          >
            <Plus className="h-5 w-5" />
            <span>Add Instructor</span>
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-dark-700 animate-slide-up">
          <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6">
            Add New Instructor
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="Instructor name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                  Expertise (comma separated) *
                </label>
                <input
                  type="text"
                  value={formData.expertise}
                  onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                  placeholder="e.g., Bridal Makeup, Hair Styling"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-charcoal-700 dark:text-charcoal-300 mb-2">
                Bio *
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                required
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                placeholder="Brief biography..."
              />
            </div>
            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20"
              >
                Add Instructor
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2.5 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Instructors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInstructors.map((instructor) => (
          <div
            key={instructor._id}
            className="group bg-white dark:bg-dark-800 rounded-3xl p-6 border border-gray-100 dark:border-dark-700 hover:shadow-xl transition-all duration-300 hover:border-rose-200 dark:hover:border-rose-900/30"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/30 dark:to-rose-800/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {instructor.profilePhoto ? (
                    <img src={instructor.profilePhoto} alt={instructor.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                      {instructor.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-charcoal-900 dark:text-white mb-1 group-hover:text-rose-600 transition-colors">
                    {instructor.name}
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {instructor.expertise.slice(0, 2).map((exp, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 rounded-lg"
                      >
                        {exp}
                      </span>
                    ))}
                    {instructor.expertise.length > 2 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-dark-700 text-charcoal-600 dark:text-charcoal-300 rounded-lg">
                        +{instructor.expertise.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button className="p-2 text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                <MoreHorizontal size={20} />
              </button>
            </div>

            <p className="text-sm text-charcoal-500 dark:text-charcoal-400 mb-6 line-clamp-3 leading-relaxed">
              {instructor.bio}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-4 text-sm font-medium text-charcoal-600 dark:text-charcoal-300">
                <div className="flex items-center gap-1.5">
                  <BookOpen size={16} className="text-charcoal-400" />
                  {instructor.coursesCount || 0}
                </div>
                <div className="flex items-center gap-1.5">
                  <Users size={16} className="text-charcoal-400" />
                  {instructor.studentsCount || 0}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/admin/instructors/edit/${instructor._id}`)}
                  className="p-2 text-charcoal-400 hover:text-blue-600 transition-colors"
                  title="Edit"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(instructor._id)}
                  className="p-2 text-charcoal-400 hover:text-red-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredInstructors.length === 0 && (
        <div className="bg-white dark:bg-dark-800 rounded-3xl p-12 border border-gray-100 dark:border-dark-700 text-center">
          <div className="w-20 h-20 bg-gray-50 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Award className="h-10 w-10 text-charcoal-400" />
          </div>
          <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2">
            No instructors found
          </h3>
          <p className="text-charcoal-500 dark:text-charcoal-400 mb-8">
            Add your first instructor to get started
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
          >
            Add Instructor
          </button>
        </div>
      )}
    </div>
  )
}
